/**
 * Permission format: resource:action
 * Wildcard '*' grants all permissions (owner/admin only).
 */
export const PERMISSIONS = {
  // Admin
  'admin:dashboard': 'View admin dashboard',
  'client:list': 'List all clients',
  'client:read': 'View client detail',
  'client:create': 'Create new client',
  'client:update': 'Update client',
  'client:delete': 'Deactivate client',
  'module:activate': 'Activate module for client',
  'security:read': 'View security monitor',
  'github:read': 'View GitHub sync status',
  'github:retry': 'Retry GitHub operations',
  'files:read': 'Browse R2 files',
  'files:write': 'Upload R2 files',
  'files:delete': 'Delete R2 files',
  'activity:read': 'View activity log',
  'activity:export': 'Export activity log',
  'invoice:manage': 'Manage invoices (mark paid, generate)',
  'change:manage': 'Manage change requests (start, complete, cancel)',
  'moderation:read': 'View moderation queue',
  'moderation:manage': 'Approve/reject moderation items',
  'onboarding:manage': 'Run client onboarding',
  'health:read': 'View system health and backup status',

  // Portal
  'own:read': 'Read own data',
  'own:update': 'Update own data',
  'invoice:read': 'View invoices',
  'change:read': 'View change requests',
  'change:create': 'Submit change request',
  'alert:read': 'View notifications',
  'alert:update': 'Mark notifications as read',

  // Modules
  'survey:read': 'View survey',
  'survey:respond': 'Submit survey responses',
  'approval:read': 'View approval items',
  'approval:respond': 'Submit approval decisions',

  // Blog
  'blog:read': 'View blog posts',
  'blog:create': 'Create blog draft',
  'blog:edit': 'Edit blog post',
  'blog:delete': 'Delete blog post',
  'blog:publish': 'Publish blog post',

  // Newsletter
  'newsletter:read': 'View newsletters',
  'newsletter:create': 'Create newsletter',
  'newsletter:edit': 'Edit newsletter',
  'newsletter:approve': 'Approve/reject newsletter',

  // Community
  'community:read': 'View community directory',
  'community:create': 'Create endorsement/recommendation',
  'community:update': 'Update community profile',
  'perk:read': 'View perks',
  'perk:create': 'Offer a perk',
  'perk:delete': 'Withdraw own perk',
  'referral:read': 'View referral data',

  // Analytics
  'analytics:read': 'View analytics/insights',
} as const

export type Permission = keyof typeof PERMISSIONS | '*'

/**
 * Default role definitions for Sitethreesixty tenant.
 * These are also stored in the tenant config JSON.
 */
export const DEFAULT_ROLES = {
  owner: {
    id: 'owner',
    label: 'Owner',
    permissions: ['*'] as Permission[],
  },
  client: {
    id: 'client',
    label: 'Client',
    permissions: [
      'own:read', 'own:update',
      'invoice:read',
      'change:read', 'change:create',
      'alert:read', 'alert:update',
      'survey:read', 'survey:respond',
      'approval:read', 'approval:respond',
      'blog:read', 'blog:create', 'blog:edit',
      'newsletter:read', 'newsletter:create', 'newsletter:edit',
      'community:read', 'community:create', 'community:update',
      'perk:read', 'perk:create', 'perk:delete',
      'referral:read',
      'analytics:read',
    ] as Permission[],
  },
} as const
